import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';
import { isInboundFinanceEmailHistoryId } from '../grid/utils/financeEmailHistoryId';
import {
  useGetFinanceEmailInboxThreadQuery,
  useMarkFinanceEmailInboxReadMutation,
} from './api/financeEmailInboxApi';
import { FinanceEmailMessageCard } from './FinanceEmailMessageCard';
import { FinanceEmailThreadToolbar } from './FinanceEmailThreadToolbar';
import type { FinanceEmailInboxMessage, FinanceEmailThreadMailboxHandlers } from './types';
import { canReplyAllToMessage } from './utils/canReplyAll';

export type { FinanceEmailThreadMailboxHandlers };

type FinanceEmailThreadDetailProps = {
  threadId: string;
  onBack: () => void;
  onReply: (historyId: string, replyAll?: boolean) => void;
  replyError?: string | null;
  isReplying?: boolean;
  showBackButton?: boolean;
  mailbox?: FinanceEmailThreadMailboxHandlers;
  /** Fired when the API resolves the canonical thread id (useful for deep links). */
  onThreadResolved?: (canonicalThreadId: string, requestedThreadId: string) => void;
};

export const FinanceEmailThreadDetail = ({
  threadId,
  onBack,
  onReply,
  replyError,
  isReplying,
  showBackButton = true,
  mailbox,
  onThreadResolved,
}: FinanceEmailThreadDetailProps) => {
  const { currentData, isError, error, refetch } = useGetFinanceEmailInboxThreadQuery(threadId);
  const thread = currentData;
  const [markRead] = useMarkFinanceEmailInboxReadMutation();
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});
  const [showTrimmed, setShowTrimmed] = useState(false);
  const [prevThreadId, setPrevThreadId] = useState(threadId);
  const autoMarkedThreadRef = useRef<string | null>(null);

  // Reset UI state when switching threads (adjust while rendering — avoids effect setState).
  if (threadId !== prevThreadId) {
    setPrevThreadId(threadId);
    setExpandedIds({});
    setShowTrimmed(false);
  }

  useEffect(() => {
    if (!currentData?.threadId) return;
    if (currentData.threadId === threadId) return;
    const matchesRequest = currentData.messages.some(
      (message) => message.historyId.toLowerCase() === threadId.toLowerCase()
    );
    if (!matchesRequest) return;
    onThreadResolved?.(currentData.threadId, threadId);
  }, [currentData, threadId, onThreadResolved]);

  useEffect(() => {
    if (!currentData?.messages?.length) return;
    if (autoMarkedThreadRef.current === threadId) return;

    const unhandledInboundIds = currentData.messages
      .filter(
        (message) =>
          (message.direction || '').toLowerCase() === 'inbound' &&
          !message.isHandled &&
          isInboundFinanceEmailHistoryId(message.historyId)
      )
      .map((message) => message.historyId);

    autoMarkedThreadRef.current = threadId;
    if (unhandledInboundIds.length === 0) return;

    void markRead({
      historyIds: unhandledInboundIds,
      threadId: currentData.threadId || threadId,
    });
  }, [currentData, markRead, threadId]);
  const latestInbound = useMemo(() => {
    if (!thread?.messages?.length) return null;
    return (
      [...thread.messages]
        .reverse()
        .find((message) => (message.direction || '').toLowerCase() === 'inbound') ?? null
    );
  }, [thread]);

  const latestInboundId = latestInbound?.historyId ?? null;
  const latestMessageId = thread?.messages?.[thread.messages.length - 1]?.historyId ?? null;

  const allHistoryIds = useMemo(
    () => (thread?.messages ?? []).map((message) => message.historyId),
    [thread]
  );

  const inboundHistoryIds = useMemo(
    () =>
      (thread?.messages ?? [])
        .filter((message) => (message.direction || '').toLowerCase() === 'inbound')
        .map((message) => message.historyId),
    [thread]
  );

  // Wait for a cache entry that matches this threadId — never paint the previous thread.
  if (!currentData) {
    if (isError) {
      return (
        <Stack spacing={2} sx={{ p: 2 }}>
          {showBackButton && (
            <Button onClick={onBack} sx={{ alignSelf: 'flex-start', textTransform: 'none' }}>
              Back to inbox
            </Button>
          )}
          <Alert
            severity="error"
            action={
              <Button color="inherit" size="small" onClick={() => void refetch()}>
                Retry
              </Button>
            }
          >
            {error instanceof Error ? error.message : 'Failed to load conversation.'}
          </Alert>
        </Stack>
      );
    }

    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!thread) {
    return null;
  }

  const isExpanded = (message: FinanceEmailInboxMessage) => {
    if (expandedIds[message.historyId] !== undefined) {
      return expandedIds[message.historyId];
    }
    return (
      message.historyId === latestInboundId ||
      message.historyId === latestMessageId ||
      thread.messages.length <= 2
    );
  };

  const messages = thread.messages;
  const shouldTrim = messages.length > 4 && !showTrimmed;
  const visibleMessages = shouldTrim ? [messages[0], ...messages.slice(-2)] : messages;
  const trimmedCount = shouldTrim ? messages.length - 3 : 0;

  const canReply =
    Boolean(latestInboundId) && isInboundFinanceEmailHistoryId(latestInboundId ?? '');
  const canReplyAll = Boolean(latestInbound && canReply && canReplyAllToMessage(latestInbound));
  const canSpam = inboundHistoryIds.length > 0;
  const canBlockSender =
    Boolean(latestInboundId) && isInboundFinanceEmailHistoryId(latestInboundId ?? '');
  const handledInboundIds = thread.messages
    .filter(
      (message) =>
        (message.direction || '').toLowerCase() === 'inbound' &&
        message.isHandled &&
        isInboundFinanceEmailHistoryId(message.historyId)
    )
    .map((message) => message.historyId);
  const canMarkUnread = handledInboundIds.length > 0;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100%' }}>
      <FinanceEmailThreadToolbar
        subject={thread.subject}
        clientName={thread.clientName}
        fromAddress={latestInbound?.fromAddress}
        messageCount={thread.messages.length}
        allHistoryIds={allHistoryIds}
        inboundHistoryIds={inboundHistoryIds}
        handledInboundIds={handledInboundIds}
        latestInboundId={latestInboundId}
        canReply={canReply}
        canReplyAll={canReplyAll}
        canSpam={canSpam}
        canBlockSender={canBlockSender}
        canMarkUnread={canMarkUnread}
        isReplying={isReplying}
        onReply={onReply}
        mailbox={mailbox}
      />

      <Stack spacing={1.25} sx={{ px: { xs: 1.5, md: 3 }, py: 2, pb: 4, flex: 1 }}>
        {replyError && <Alert severity="error">{replyError}</Alert>}

        {visibleMessages.map((message, index) => {
          const showTrimButton = shouldTrim && index === 0;
          return (
            <Box key={message.historyId}>
              <FinanceEmailMessageCard
                message={message}
                expanded={isExpanded(message)}
                isLatest={message.historyId === latestInboundId}
                onToggle={() =>
                  setExpandedIds((current) => ({
                    ...current,
                    [message.historyId]: !isExpanded(message),
                  }))
                }
                onReply={onReply}
                isReplying={isReplying}
              />
              {showTrimButton && (
                <Box sx={{ display: 'flex', justifyContent: 'center', my: 1 }}>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setShowTrimmed(true)}
                    sx={{
                      textTransform: 'none',
                      borderRadius: 5,
                      px: 2,
                      bgcolor: 'background.paper',
                      border: '1px solid',
                      borderColor: 'divider',
                      color: 'text.secondary',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  >
                    {trimmedCount} older message{trimmedCount === 1 ? '' : 's'}
                  </Button>
                </Box>
              )}
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};
